import { useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { dashboardAPI } from '../utils/api';

/**
 * Real-time financial summary observer
 * Monitors all financial tables for changes and automatically updates daily summary
 */
export const useFinancialSummaryObserver = (targetDate) => {
  const isInitialized = useRef(false);
  const timeoutRef = useRef(null);

  // Debounced save function to prevent multiple rapid calls
  const saveWithDebounce = (date) => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(async () => {
      try {
        await dashboardAPI.saveFinancialSummary(date);
      } catch (error) {
        // Silent fail - don't log errors
      }
    }, 1000); // Wait 1 second after last change
  };

  useEffect(() => {
    if (!targetDate || isInitialized.current) return;

    const dateStr = targetDate.toISOString().split('T')[0];

    // Initialize: Update immediately when observer starts
    saveWithDebounce(dateStr);

    // Define tables to monitor for financial changes
    const tables = [
      { name: 'sales', schema: 'public' },
      { name: 'car_sales', schema: 'public' },
      { name: 'service_transactions', schema: 'public' },
      { name: 'daily_expenses', schema: 'public' },
      { name: 'balance_transactions', schema: 'public' },
      { name: 'employee_bonuses', schema: 'public' }
    ];

    // Create subscriptions for all tables
    const subscriptions = [];

    tables.forEach(table => {
      const subscription = supabase
        .channel(`${table.schema}-${table.name}-changes`)
        .on(
          'postgres_changes',
          {
            event: '*', // Listen for all events (INSERT, UPDATE, DELETE)
            schema: table.schema,
            table: table.name
          },
          (payload) => {
            // For delete operations, we might need to check the old record's date
            if (payload.eventType === 'DELETE' && payload.old) {
              // Check if the deleted record has a date field
              if (payload.old.sale_date) {
                const saleDate = new Date(payload.old.sale_date);
                saveWithDebounce(saleDate);
              } else if (payload.old.date) {
                const recordDate = new Date(payload.old.date);
                saveWithDebounce(recordDate);
              } else if (payload.old.transaction_date) {
                const transactionDate = new Date(payload.old.transaction_date);
                saveWithDebounce(transactionDate);
              } else if (payload.old.bonus_date) {
                const bonusDate = new Date(payload.old.bonus_date);
                saveWithDebounce(bonusDate);
              }
            }

            // For INSERT/UPDATE operations, use the new record's date
            if (payload.new) {
              let changeDate = targetDate; // Default to observer date

              // Check various date fields
              if (payload.new.sale_date) {
                changeDate = new Date(payload.new.sale_date);
              } else if (payload.new.date) {
                changeDate = new Date(payload.new.date);
              } else if (payload.new.transaction_date) {
                changeDate = new Date(payload.new.transaction_date);
              } else if (payload.new.bonus_date) {
                changeDate = new Date(payload.new.bonus_date);
              }

              saveWithDebounce(changeDate);
            }
          }
        )
        .subscribe();

      subscriptions.push(subscription);
    });

    // Mark as initialized
    isInitialized.current = true;

    // Cleanup function
    return () => {
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Unsubscribe from all channels
      subscriptions.forEach(subscription => {
        supabase.removeChannel(subscription);
      });

      isInitialized.current = false;
    };
  }, [targetDate]); // Re-initialize when target date changes

  // Manual trigger function (for external calls)
  const triggerUpdate = () => {
    if (targetDate) {
      saveWithDebounce(targetDate);
    }
  };

  return {
    triggerUpdate,
    isActive: isInitialized.current
  };
};
